/** Device identity: key generation, persistence and DeviceInfo projection. */
import type { DeviceInfo } from '@luminis/protocol';
import { generateExchangeKeyPair, generateSigningKeyPair } from './crypto/e2e';
import { base64UrlEncode, nowSeconds, randomBytes } from './id';
import { loadJson, removeKey, saveJson } from './storage';
import type { DeviceIdentityStored } from './types';

const IDENTITY_KEY = 'device_identity';

export function detectPlatform(): string {
  try {
    const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
    if (/android/iu.test(ua)) return 'android';
    if (/iphone|ipad|ipod/iu.test(ua)) return 'ios';
    if (/windows/iu.test(ua)) return 'windows';
    if (/mac os x|macintosh/iu.test(ua)) return 'macos';
    if (/linux/iu.test(ua)) return 'linux';
    return 'web';
  } catch {
    return 'web';
  }
}

export function createIdentity(name: string): DeviceIdentityStored {
  const exchange = generateExchangeKeyPair();
  const signing = generateSigningKeyPair();
  const deviceId = `dev_${base64UrlEncode(randomBytes(12))}`;
  return {
    device_id: deviceId,
    name: name.trim() || '我的手机',
    platform: detectPlatform(),
    e2e_private_key: exchange.privateKey,
    e2e_public_key: exchange.publicKey,
    signing_private_key: signing.privateKey,
    signing_public_key: signing.publicKey,
    created_at: nowSeconds(),
  };
}

function isIdentity(value: unknown): value is DeviceIdentityStored {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.device_id === 'string' &&
    /^dev_[A-Za-z0-9_-]{6,64}$/u.test(candidate.device_id) &&
    typeof candidate.name === 'string' &&
    typeof candidate.platform === 'string' &&
    typeof candidate.e2e_private_key === 'string' &&
    typeof candidate.e2e_public_key === 'string' &&
    typeof candidate.signing_private_key === 'string' &&
    typeof candidate.signing_public_key === 'string'
  );
}

export function loadIdentity(): DeviceIdentityStored | null {
  const stored = loadJson<unknown>(IDENTITY_KEY, null);
  return isIdentity(stored) ? stored : null;
}

export function saveIdentity(identity: DeviceIdentityStored): void {
  saveJson(IDENTITY_KEY, identity);
}

export function clearIdentity(): void {
  removeKey(IDENTITY_KEY);
}

export function deviceInfoForPairing(identity: DeviceIdentityStored): DeviceInfo {
  return {
    device_id: identity.device_id,
    name: identity.name,
    platform: identity.platform,
    // X25519 key: derives the E2E channel key during pairing.
    public_key: identity.e2e_public_key,
    // Ed25519 key: lets the PC daemon verify signed approval decisions.
    signing_public_key: identity.signing_public_key,
  };
}
